// События аналитики клиента (E18-T02; tech/09 §1 п. 3, §2, §3.1, §3.5, §3.8–3.9). У каждого — event_id (ULID) для
// дедупликации и seq внутри сессии; session_id создаётся при запуске. Отправка — пачками до 100 событий не чаще раза
// в 30 с, при уходе в фон — сразу через navigator.sendBeacon. Пачка, не дошедшая до сервера, отправляется снова теми же
// event_id — сервер отбрасывает повторы. Транспорт подключаемый: эндпоинт POST /analytics/batch ждёт договора
// (CONTRACT Р-К103); до него — приёмник (dev — консоль, тесты — массив).
export interface AnalyticsEvent {
  eventId: string;
  name: string;
  seq: number;
  ts: number;
  props: Record<string, unknown>;
}

/** Транспорт: send — обычная отправка (обещание отклоняется при сбое), beacon — при уходе в фон (true — принято). */
export interface AnalyticsTransport {
  send(sessionId: string, batch: AnalyticsEvent[]): Promise<void>;
  beacon(sessionId: string, batch: AnalyticsEvent[]): boolean;
}

type Sink = (batch: AnalyticsEvent[]) => void;

export const BATCH_MAX = 100;
export const BATCH_EVERY_MS = 30_000;
const MAX_QUEUE = 500;

/**
 * Клиентские события словаря tech/09 §3 (source: client) — остальные клиент не шлёт: сервер отбросил бы их
 * (неизвестное событие → analytics_rejected_total). Факты, которые пишет сервер, клиент не дублирует (§1 п. 1).
 */
export const CLIENT_EVENTS = new Set([
  'app_open', 'load_step', 'session_start', 'session_end', 'screen_view', 'system_screen_view', 'tutorial_step_start',
  'fullscreen_toggle', 'ws_state', 'api_error', 'client_error', 'perf_sample', 'invoice_opened', 'invoice_closed',
  'shop_view', 'shop_product_click', 'showcase_view', 'offer_show', 'leaderboard_view', 'story_scene_view',
  'content_release_loaded', 'content_load_failed', 'content_string_missing', 'ad_shown', 'ad_complete', 'ad_fail',
]);

/** ULID (Crockford base32: 10 знаков времени + 16 случайных), монотонный внутри миллисекунды. */
const B32 = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
let lastMs = 0;
let lastRand: number[] = [];
export function ulid(now = Date.now(), rand: () => number = Math.random): string {
  let t = '';
  let ms = now;
  for (let i = 0; i < 10; i++) { t = B32[ms % 32] + t; ms = Math.floor(ms / 32); }
  if (now === lastMs && lastRand.length) {
    // та же миллисекунда — +1 к случайной части: порядок сохраняется
    for (let i = 15; i >= 0; i--) { if (lastRand[i]! < 31) { lastRand[i]!++; break; } lastRand[i] = 0; }
  } else {
    lastMs = now;
    lastRand = Array.from({ length: 16 }, () => Math.floor(rand() * 32));
  }
  return t + lastRand.map((n) => B32[n]).join('');
}

export const sessionId = ulid();
let seq = 0;
const queue: AnalyticsEvent[] = [];
let inFlight: AnalyticsEvent[] | null = null;
let sink: Sink | null = null;
let transport: AnalyticsTransport | null = null;
let timer: ReturnType<typeof setTimeout> | null = null;
let lastSent = 0;

export function setAnalyticsSink(s: Sink | null): void {
  sink = s;
  flush();
}

export function setAnalyticsTransport(t: AnalyticsTransport | null): void {
  transport = t;
  schedule();
}

export function track(name: string, props: Record<string, unknown> = {}): void {
  queue.push({ eventId: ulid(), name, seq: seq++, ts: Date.now(), props });
  if (queue.length > MAX_QUEUE) queue.splice(0, queue.length - MAX_QUEUE);
  if (sink) queueMicrotask(flush);
  else schedule();
}

/** Плановая отправка: не чаще раза в 30 с; полная пачка (100) — в ближайшее окно. */
function schedule(): void {
  if (!transport || timer || !queue.length) return;
  const wait = Math.max(0, lastSent + BATCH_EVERY_MS - Date.now());
  timer = setTimeout(() => { timer = null; void sendNow(); }, wait);
}

/** Только клиентские события словаря; уходят в транспорт. */
function takeBatch(): AnalyticsEvent[] {
  const out: AnalyticsEvent[] = [];
  while (queue.length && out.length < BATCH_MAX) {
    const e = queue.shift()!;
    if (CLIENT_EVENTS.has(e.name)) out.push(e);
  }
  return out;
}

export async function sendNow(): Promise<void> {
  if (!transport || inFlight) return;
  const batch = takeBatch();
  if (!batch.length) return;
  inFlight = batch;
  lastSent = Date.now();
  try {
    await transport.send(sessionId, batch);
  } catch {
    // Сбой сети: вернуть пачку в начало очереди — повтор теми же event_id (дубли отсеет сервер).
    queue.unshift(...batch);
  } finally {
    inFlight = null;
    schedule();
  }
}

/** Уход в фон или закрытие: всё, что в очереди, — через sendBeacon пачками по 100 (tech/09 §1 п. 3). */
export function flushBeacon(): void {
  if (!transport) return;
  if (timer) { clearTimeout(timer); timer = null; }
  while (queue.length) {
    const batch = takeBatch();
    if (!batch.length) break;
    if (!transport.beacon(sessionId, batch)) { queue.unshift(...batch); break; }
  }
}

/** Приёмник без транспорта (dev, тесты) — получает всё сразу. */
export function flush(): void {
  if (!sink || queue.length === 0) return;
  sink(queue.splice(0, queue.length));
}

/** Для тестов и диагностики: события, ещё не отданные. */
export function pending(): readonly AnalyticsEvent[] {
  return queue;
}

/** Только для тестов: пустая очередь и окно отправки. */
export function resetAnalytics(): void {
  queue.length = 0;
  inFlight = null;
  lastSent = 0;
  if (timer) { clearTimeout(timer); timer = null; }
}
